/**
 * Belt Helper Functions
 *
 * Centralizes all belt-related data transformations, validations, and sorting.
 * Addresses D1-D8 data-related issues identified in the belt promotion fix plan.
 *
 * @see /docs/BELT_PROMOTION_FIX_PLAN.md for detailed issue descriptions
 */

import { BeltConfig } from '../types/belt';
import { formatOrdinal } from './formatting';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Martial art name aliases for normalization
 * Maps common variations to canonical names
 */
export const MARTIAL_ART_ALIASES: Record<string, string> = {
  'bjj': 'Brazilian Jiu Jitsu',
  'jiu jitsu': 'Brazilian Jiu Jitsu',
  'jiu-jitsu': 'Brazilian Jiu Jitsu',
  'brazilian jiu-jitsu': 'Brazilian Jiu Jitsu',
  'brazilian jiu jitsu': 'Brazilian Jiu Jitsu',
  'judo': 'Judo',
  'karate': 'Karate',
  'taekwondo': 'Taekwondo',
  'tae kwon do': 'Taekwondo',
  'tkd': 'Taekwondo',
  'muay thai': 'Muay Thai',
  'kickboxing': 'Kickboxing',
  'wrestling': 'Wrestling',
  'mma': 'MMA',
  'mixed martial arts': 'MMA',
};

/**
 * Belt rank ordering for sorting (BJJ standard + common martial arts)
 * Higher number = higher rank
 */
export const BELT_ORDER: Record<string, number> = {
  'white': 0,
  'grey': 1,
  'gray': 1,   // Alternative spelling
  'yellow': 2,
  'orange': 3,
  'green': 4,
  'blue': 5,
  'purple': 6,
  'brown': 7,
  'black': 8,
  'red-black': 9,   // Coral belt (7th degree)
  'red-white': 10,  // Coral belt (8th degree)
  'red': 11,        // Grand Master (9th-10th degree)
};

/**
 * Default belt configuration with all required fields
 * Used as fallback when beltConfig is incomplete
 */
export const DEFAULT_BELT_CONFIG: BeltConfig = {
  name: 'White Belt',
  baseColor: 'white',
  rank: '',
  level: 'adult',
  stripeCount: 0,
  contrastColor: '#FFFFFF',
  contrastType: 'solid',
  stripeColor: '#FFFFFF',
  stripeBgColor: 'black',
};

// =============================================================================
// D1: MARTIAL ART NORMALIZATION AND VALIDATION
// =============================================================================

/**
 * Normalizes and validates martial art name
 *
 * D1 Fix: Empty martialArt would pass silently and break notifications/profile sync.
 * This function throws an error for empty values and normalizes common variations.
 *
 * @param input - The martial art name to normalize
 * @throws Error if input is empty or whitespace-only
 * @returns Normalized martial art name
 *
 * @example
 * normalizeMartialArt('bjj') // returns 'Brazilian Jiu Jitsu'
 * normalizeMartialArt('') // throws Error
 */
export function normalizeMartialArt(input: string | undefined | null): string {
  if (!input || input.trim().length === 0) {
    throw new Error('Martial art is required');
  }

  const trimmed = input.trim();
  const lower = trimmed.toLowerCase();

  // Return canonical name if alias exists, otherwise return trimmed input
  return MARTIAL_ART_ALIASES[lower] || trimmed;
}

/**
 * Safely normalizes martial art without throwing (for display purposes)
 * Returns a default value instead of throwing for invalid input
 *
 * @param input - The martial art name to normalize
 * @param defaultValue - Value to return if input is empty (default: 'Unknown')
 * @returns Normalized martial art name or default
 */
export function normalizeMartialArtSafe(
  input: string | undefined | null,
  defaultValue: string = 'Unknown'
): string {
  if (!input || input.trim().length === 0) {
    return defaultValue;
  }

  const trimmed = input.trim();
  const lower = trimmed.toLowerCase();

  return MARTIAL_ART_ALIASES[lower] || trimmed;
}

// =============================================================================
// D2: STRIPE COUNT PARSING
// =============================================================================

/**
 * Parses stripe count to a valid number with bounds checking
 *
 * D2 Fix: stripeCount could be string or NaN causing string concatenation bugs:
 * - `"2" + 1` = `"21"` instead of `3`
 * - `NaN > 0` = `false` breaking comparisons
 *
 * @param value - The value to parse (string, number, or unknown)
 * @param defaultValue - Value to return for invalid input (default: 0)
 * @param maxStripes - Maximum allowed stripes (default: 10 for black belt compatibility)
 * @returns Parsed stripe count, clamped to [0, maxStripes]
 *
 * @example
 * parseStripeCount('2') // returns 2
 * parseStripeCount('abc') // returns 0
 * parseStripeCount(15) // returns 10 (clamped)
 * parseStripeCount(undefined) // returns 0
 */
export function parseStripeCount(
  value: unknown,
  defaultValue: number = 0,
  maxStripes: number = 10
): number {
  let parsed: number;

  if (typeof value === 'number') {
    parsed = value;
  } else if (typeof value === 'string') {
    parsed = parseInt(value, 10);
  } else {
    return defaultValue;
  }

  // Handle NaN
  if (isNaN(parsed)) {
    return defaultValue;
  }

  // Clamp to valid range [0, maxStripes]
  return Math.max(0, Math.min(maxStripes, Math.floor(parsed)));
}

/**
 * Gets the maximum stripe count for a belt color
 * Black belts can have up to 10 stripes (degrees), others max at 4
 *
 * @param beltColor - The belt color
 * @returns Maximum stripe count (4 for most belts, 10 for black)
 */
export function getMaxStripes(beltColor: string | undefined): number {
  if (!beltColor) return 4;
  const lower = beltColor.toLowerCase();
  return lower === 'black' || lower.includes('red') ? 10 : 4;
}

// =============================================================================
// D3: NOTES FIELD HANDLING
// =============================================================================

/**
 * Extracts notes from promotion data
 *
 * D3 Fix: Phoenix uses 'notes' but Firebase uses 'note'. Data could be lost
 * on edit if we only check one field.
 *
 * @param data - Object that may contain notes or note field
 * @returns The notes value, or empty string if not found
 *
 * @example
 * getNotes({ notes: 'Great promotion!' }) // returns 'Great promotion!'
 * getNotes({ note: 'Legacy note' }) // returns 'Legacy note'
 * getNotes({}) // returns ''
 */
export function getNotes(data: Record<string, unknown> | null | undefined): string {
  if (!data) return '';

  // Prefer 'notes' (Phoenix format) over 'note' (Firebase format)
  const notes = data.notes ?? data.note;

  if (typeof notes === 'string') {
    return notes;
  }

  return '';
}

// =============================================================================
// D5: BELT CONFIG COMPLETION
// =============================================================================

/**
 * Completes a partial belt config with defaults
 *
 * D5 Fix: Custom belts or partial configs could be missing required fields
 * like contrastColor or contrastType, causing display issues.
 *
 * @param partial - Partial belt config (may be undefined or missing fields)
 * @returns Complete belt config with all required fields
 *
 * @example
 * completeBeltConfig({ baseColor: 'purple' })
 * // returns { name: 'White Belt', baseColor: 'purple', level: 'adult', ... }
 */
export function completeBeltConfig(
  partial: Partial<BeltConfig> | undefined | null
): BeltConfig {
  if (!partial) {
    return { ...DEFAULT_BELT_CONFIG };
  }

  return {
    ...DEFAULT_BELT_CONFIG,
    ...partial,
    // Ensure stripeCount is always a valid number
    stripeCount: parseStripeCount(partial.stripeCount),
    // Ensure level is valid
    level: partial.level === 'junior' ? 'junior' : 'adult',
  };
}

/**
 * Builds a complete belt config from promotion data
 * Handles various field naming conventions
 *
 * @param beltColor - The belt color
 * @param stripes - Number of stripes
 * @param providedConfig - Optional partial config to merge
 * @returns Complete belt config
 */
export function buildBeltConfig(
  beltColor: string,
  stripes: number,
  providedConfig?: Partial<BeltConfig> | null
): BeltConfig {
  const color = beltColor.toLowerCase();
  const stripeCount = parseStripeCount(stripes, 0, getMaxStripes(color));

  // Build name like "Purple Belt" or "2nd Stripe Purple Belt"
  const capitalizedColor = color.charAt(0).toUpperCase() + color.slice(1);
  const baseName = `${capitalizedColor} Belt`;
  const rankName = stripeCount > 0
    ? `${formatOrdinal(stripeCount)} Stripe`
    : '';

  const base: BeltConfig = {
    name: baseName,
    baseColor: color,
    rank: rankName,
    level: 'adult',
    stripeCount,
    contrastColor: color === 'black' ? 'red' : 'white',
    contrastType: 'solid',
    stripeColor: '#FFFFFF',
    stripeBgColor: color === 'black' ? 'red' : 'black',
  };

  // Merge with provided config, preserving stripeCount from above
  if (providedConfig) {
    return {
      ...base,
      ...providedConfig,
      stripeCount, // Always use parsed stripe count
    };
  }

  return base;
}

// =============================================================================
// D6: DETERMINISTIC MARTIAL ART ID
// =============================================================================

/**
 * Generates a deterministic ID for a martial art
 *
 * D6 Fix: updateUserBeltStatus was using `martialArt-${Date.now()}` which
 * creates new IDs every time, causing duplicate entries in user profile.
 *
 * @param martialArt - The martial art name
 * @returns Consistent ID in format: "martialArt-{normalized-name}"
 *
 * @example
 * getMartialArtId('Brazilian Jiu Jitsu') // returns 'martialArt-brazilian-jiu-jitsu'
 * getMartialArtId('BJJ') // returns 'martialArt-brazilian-jiu-jitsu' (normalized)
 */
export function getMartialArtId(martialArt: string): string {
  const normalized = normalizeMartialArtSafe(martialArt, 'unknown');
  const slug = normalized.toLowerCase().replace(/\s+/g, '-');
  return `martialArt-${slug}`;
}

/**
 * Finds an existing martial art ID in profile data
 * Uses normalized name comparison to handle variations
 *
 * @param martialArts - The user's martial arts object
 * @param martialArt - The martial art to find
 * @returns Existing ID if found, otherwise generates new deterministic ID
 */
export function findOrCreateMartialArtId(
  martialArts: Record<string, { name?: string; martialArt?: string }> | undefined,
  martialArt: string
): string {
  if (!martialArts) {
    return getMartialArtId(martialArt);
  }

  const normalizedTarget = normalizeMartialArtSafe(martialArt).toLowerCase();

  // Try to find existing entry with matching name
  const existingId = Object.keys(martialArts).find(key => {
    const entry = martialArts[key];
    const entryName = (entry.name || entry.martialArt || '').toLowerCase();
    const normalizedEntry = normalizeMartialArtSafe(entryName).toLowerCase();
    return normalizedEntry === normalizedTarget;
  });

  return existingId || getMartialArtId(martialArt);
}

// =============================================================================
// D7: PHOTO NORMALIZATION
// =============================================================================

/**
 * Photo object structure for Firebase storage
 */
export interface NormalizedPhoto {
  url: string;
  aspectRatio: number;
}

/**
 * Normalizes photo data to consistent object format
 *
 * D7 Fix: Phoenix uses `photoURL` (string) but Firebase stores `photo` (object).
 * Inconsistent handling caused photos to not display after edit.
 *
 * @param data - Object that may contain photoURL or photo field
 * @returns Normalized photo object or null if no photo
 *
 * @example
 * normalizePhoto({ photoURL: 'https://...' }) // returns { url: 'https://...', aspectRatio: 1 }
 * normalizePhoto({ photo: { url: 'https://...', aspectRatio: 1.5 } }) // returns { url: 'https://...', aspectRatio: 1.5 }
 * normalizePhoto({}) // returns null
 */
export function normalizePhoto(
  data: {
    photoURL?: string;
    photo?: { url?: string; aspectRatio?: number } | null;
  } | null | undefined
): NormalizedPhoto | null {
  if (!data) return null;

  // Check for string photoURL first
  if (data.photoURL && typeof data.photoURL === 'string' && data.photoURL.trim()) {
    return {
      url: data.photoURL.trim(),
      aspectRatio: 1,
    };
  }

  // Check for photo object
  if (data.photo?.url && typeof data.photo.url === 'string' && data.photo.url.trim()) {
    return {
      url: data.photo.url.trim(),
      aspectRatio: data.photo.aspectRatio ?? 1,
    };
  }

  return null;
}

/**
 * Extracts photo URL from promotion data (for display purposes)
 *
 * @param data - Object that may contain photo data
 * @returns Photo URL string or null
 */
export function getPhotoURL(
  data: {
    photoURL?: string;
    photo?: { url?: string } | null;
  } | null | undefined
): string | null {
  const photo = normalizePhoto(data);
  return photo?.url || null;
}

// =============================================================================
// D8: OPTIONAL FIELD DEFAULTS
// =============================================================================

/**
 * Gets academy with proper fallback
 *
 * D8 Fix: Academy field could be saved as undefined instead of empty string
 * when using `||` instead of `??`
 *
 * @param primary - Primary value
 * @param fallback - Fallback value
 * @returns String value, never undefined
 */
export function getAcademy(
  primary: string | undefined | null,
  fallback?: string | undefined | null
): string {
  return primary ?? fallback ?? '';
}

/**
 * Gets instructor with proper fallback
 *
 * @param primary - Primary value
 * @param fallback - Fallback value
 * @returns String value, never undefined
 */
export function getInstructor(
  primary: string | undefined | null,
  fallback?: string | undefined | null
): string {
  return primary ?? fallback ?? '';
}

// =============================================================================
// BELT SORTING
// =============================================================================

/**
 * Calculates sort value for a belt promotion
 * Combines belt rank and stripe count into a single sortable number
 *
 * @param promotion - Belt promotion or partial data
 * @returns Sort value (higher = higher rank)
 *
 * @example
 * getBeltSortValue({ beltColor: 'white', stripes: 0 }) // returns 0
 * getBeltSortValue({ beltColor: 'blue', stripes: 2 }) // returns 502
 * getBeltSortValue({ beltColor: 'black', stripes: 5 }) // returns 805
 */
export function getBeltSortValue(
  promotion: {
    beltColor?: string;
    stripes?: number;
    beltConfig?: Partial<BeltConfig>;
  } | null | undefined
): number {
  if (!promotion) return 0;

  const beltColor = (
    promotion.beltColor ||
    promotion.beltConfig?.baseColor ||
    'white'
  ).toLowerCase();

  const beltRank = BELT_ORDER[beltColor] ?? 0;
  const stripes = parseStripeCount(
    promotion.stripes ?? promotion.beltConfig?.stripeCount
  );

  // Combine: beltRank * 100 + stripes
  // This allows for up to 99 stripes per rank (more than enough)
  return beltRank * 100 + stripes;
}

/**
 * Sorts belt promotions by rank (highest first) and date (oldest first for same rank)
 *
 * @param promotions - Array of promotions to sort
 * @returns New sorted array (does not mutate original)
 *
 * @example
 * sortPromotionsByRank([
 *   { beltColor: 'blue', stripes: 2, date: 1000 },
 *   { beltColor: 'white', stripes: 4, date: 500 },
 * ])
 * // returns [white 4-stripe, blue 2-stripe] (by rank ascending)
 */
export function sortPromotionsByRank<T extends {
  beltColor?: string;
  stripes?: number;
  date?: number;
  beltConfig?: Partial<BeltConfig>;
}>(promotions: T[]): T[] {
  return [...promotions].sort((a, b) => {
    // Primary sort by rank+stripes (ascending - lowest rank first for timeline)
    const rankDiff = getBeltSortValue(a) - getBeltSortValue(b);
    if (rankDiff !== 0) return rankDiff;

    // Secondary sort by date (oldest first)
    return (a.date ?? 0) - (b.date ?? 0);
  });
}

/**
 * Sorts belt promotions by rank descending (highest first)
 * Useful for displaying current belt at top
 *
 * @param promotions - Array of promotions to sort
 * @returns New sorted array (does not mutate original)
 */
export function sortPromotionsByRankDescending<T extends {
  beltColor?: string;
  stripes?: number;
  date?: number;
  beltConfig?: Partial<BeltConfig>;
}>(promotions: T[]): T[] {
  return [...promotions].sort((a, b) => {
    // Primary sort by rank+stripes (descending - highest rank first)
    const rankDiff = getBeltSortValue(b) - getBeltSortValue(a);
    if (rankDiff !== 0) return rankDiff;

    // Secondary sort by date (newest first)
    return (b.date ?? 0) - (a.date ?? 0);
  });
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Gets ordinal suffix for a number (1st, 2nd, 3rd, 4th, etc.)
 * Re-exported from formatting.ts for backwards compatibility
 *
 * @param n - The number
 * @returns Ordinal string (e.g., "1st", "2nd", "3rd")
 */
export { formatOrdinal as getOrdinalSuffix } from './formatting';

/**
 * Compares two belts by rank
 *
 * @param a - First belt
 * @param b - Second belt
 * @returns Negative if a < b, positive if a > b, 0 if equal
 */
export function compareBeltRanks(
  a: { beltColor?: string; stripes?: number; beltConfig?: Partial<BeltConfig> },
  b: { beltColor?: string; stripes?: number; beltConfig?: Partial<BeltConfig> }
): number {
  return getBeltSortValue(a) - getBeltSortValue(b);
}

/**
 * Checks if a belt is higher rank than another
 *
 * @param candidate - The belt to check
 * @param reference - The reference belt to compare against
 * @returns True if candidate is higher rank than reference
 */
export function isHigherRank(
  candidate: { beltColor?: string; stripes?: number; beltConfig?: Partial<BeltConfig> },
  reference: { beltColor?: string; stripes?: number; beltConfig?: Partial<BeltConfig> }
): boolean {
  return compareBeltRanks(candidate, reference) > 0;
}

/**
 * Gets the current/highest belt from a list of promotions
 *
 * @param promotions - List of promotions
 * @returns The highest ranked promotion, or undefined if empty
 */
export function getCurrentBelt<T extends {
  beltColor?: string;
  stripes?: number;
  beltConfig?: Partial<BeltConfig>;
}>(promotions: T[]): T | undefined {
  if (!promotions.length) return undefined;

  return promotions.reduce((highest, current) => {
    return isHigherRank(current, highest) ? current : highest;
  });
}
